import {
  CONFIRMED_POSITION_TRANSITION_WINDOW_MS,
  type GetMarketRow,
  type GetWinnerRow,
  isUserPortfolioSnapshotWire,
  type QueryClient,
  readPositionEntity,
  type UserPortfolioPositionWire,
  type UserPortfolioUnvalidatedValue,
} from '@morfi/backend-sdk-v2/client';
import { getActiveNetwork } from '@/lib/network/active';
import { logger } from '@/lib/telemetry/logger';
import {
  type ConfirmedPositionTrade,
  type ConfirmedPositionUpdater,
  estimateConfirmedPosition,
} from './confirmed-position-accounting';
import { userPortfolioSnapshotQueryKey } from './query-key';

export interface ConfirmedPositionTransition {
  readonly row: UserPortfolioPositionWire;
}
export const CONFIRMED_POSITION_META = { persist: false, viewerSensitive: true };
export const EMPTY_TRANSITIONS: readonly ConfirmedPositionTransition[] = [];

export function confirmedPositionQueryKey(userId: string, network: string): readonly string[] {
  return ['confirmed-position-transitions', network, userId];
}

/** Capture before submit, publish only after landing. Never modify the saved portfolio DTO. */
export function prepareConfirmedPositionUpdate(
  queryClient: QueryClient,
  userId: string,
): ConfirmedPositionUpdater {
  const network = getActiveNetwork();
  const snapshotKey = userPortfolioSnapshotQueryKey(userId);
  const owner = queryClient.getQueryCache().find({ queryKey: snapshotKey, exact: true });
  const captured = queryClient.getQueryData<UserPortfolioUnvalidatedValue>(snapshotKey);
  const applied = new Set<string>();
  return (trade: ConfirmedPositionTrade): void => {
    const identity = `${trade.walletAddress}:${trade.outcomeMint}`;
    if (applied.has(identity)) return;
    applied.add(identity);
    if (
      getActiveNetwork() !== network ||
      owner !== queryClient.getQueryCache().find({ queryKey: snapshotKey, exact: true })
    )
      return;
    const current = queryClient.getQueryData<UserPortfolioUnvalidatedValue>(snapshotKey);
    if (!isUserPortfolioSnapshotWire(captured) || !isUserPortfolioSnapshotWire(current)) {
      logger.warn('trade.position_transition_unavailable', { reason: 'snapshot_unavailable' });
      reconcile(queryClient, userId);
      return;
    }
    if (!captured.wallets.includes(trade.walletAddress)) return;
    const base = findPosition(captured.positions, trade);
    const authoritative = findPosition(current.positions, trade);
    const key = confirmedPositionQueryKey(userId, network);
    const transitions =
      queryClient.getQueryData<readonly ConfirmedPositionTransition[]>(key) ?? EMPTY_TRANSITIONS;
    const pending = transitions.find((entry) => samePosition(entry.row, trade));
    // A newer authoritative row may already include this execution. Never add it twice.
    if ((authoritative?.tradeCount ?? 0) > (base?.tradeCount ?? 0)) {
      reconcile(queryClient, userId);
      return;
    }
    const previous =
      pending !== undefined && pending.row.tradeCount > (base?.tradeCount ?? 0)
        ? pending.row
        : base;
    const row = estimateConfirmedPosition(previous, trade);
    if (row === null) {
      logger.warn('trade.position_transition_unavailable', { reason: 'invalid_accounting' });
      reconcile(queryClient, userId);
      return;
    }
    const transition: ConfirmedPositionTransition = {
      row: withIndexedIdentity(queryClient, row),
    };
    queryClient.setQueryDefaults(key, { meta: CONFIRMED_POSITION_META });
    const next = [...transitions.filter((entry) => !samePosition(entry.row, trade)), transition];
    // ALLOW: this helper exclusively owns confirmed-position-transitions, never portfolio DTOs.
    // REASON: viewer/network-scoped estimates are non-persisted and bounded to the confirmation window.
    queryClient.setQueryData(key, next); // ALLOW-STATE-OWNERSHIP: exact confirmed-position overlay owner
    reconcile(queryClient, userId);
    setTimeout(() => {
      if (
        queryClient.getQueryData<readonly ConfirmedPositionTransition[]>(key)?.includes(transition)
      ) {
        reconcile(queryClient, userId);
      }
    }, 2000);
    setTimeout(() => {
      const entries = queryClient.getQueryData<readonly ConfirmedPositionTransition[]>(key);
      if (entries === undefined || !entries.includes(transition)) return;
      const latest = queryClient.getQueryData<UserPortfolioUnvalidatedValue>(snapshotKey);
      const settled = isUserPortfolioSnapshotWire(latest)
        ? findPosition(latest.positions, trade)
        : undefined;
      if ((settled?.tradeCount ?? 0) < row.tradeCount) {
        logger.warn('trade.position_transition_expired', { outcomeIndex: trade.outcomeIndex });
        reconcile(queryClient, userId);
      }
      const next = entries.filter((entry) => entry !== transition);
      // ALLOW: this helper expires only its own confirmed-position overlay entry.
      // REASON: authoritative portfolio data remains untouched when the estimate expires.
      queryClient.setQueryData(key, next); // ALLOW-STATE-OWNERSHIP: exact confirmed-position overlay owner
    }, CONFIRMED_POSITION_TRANSITION_WINDOW_MS);
  };
}

export function samePosition(
  row: UserPortfolioPositionWire,
  trade: Pick<ConfirmedPositionTrade, 'walletAddress' | 'market' | 'outcomeMint'>,
): boolean {
  return (
    row.wallet === trade.walletAddress &&
    row.market === trade.market &&
    row.outcomeMint === trade.outcomeMint
  );
}

function findPosition(
  rows: readonly UserPortfolioPositionWire[],
  trade: ConfirmedPositionTrade,
): UserPortfolioPositionWire | undefined {
  return rows.find((row) => samePosition(row, trade));
}

function reconcile(queryClient: QueryClient, userId: string): void {
  const queryKey = userPortfolioSnapshotQueryKey(userId);
  // ALLOW: confirmation requests reconciliation from the existing portfolio snapshot owner.
  // REASON: only the exact user's snapshot is refreshed; no canonical values are patched.
  void queryClient.invalidateQueries({ queryKey, exact: true }); // ALLOW-STATE-OWNERSHIP: exact snapshot reconciliation
}

function withIndexedIdentity(
  queryClient: QueryClient,
  row: UserPortfolioPositionWire,
): UserPortfolioPositionWire {
  const indexed = readPositionEntity(queryClient, row.positionId);
  const outcome = queryClient.getQueryData<Partial<GetWinnerRow>>([
    'outcomes',
    'entity',
    row.outcomeMint,
  ]);
  const market = queryClient.getQueryData<Partial<GetMarketRow>>(['markets', 'entity', row.market]);
  return {
    ...row,
    marketType: market?.marketType ?? indexed?.marketType ?? row.marketType ?? 'ordinary',
    partnerCreatorId:
      market?.partnerCreatorId ?? indexed?.partnerCreatorId ?? row.partnerCreatorId ?? null,
    associatedCreator:
      market?.associatedCreator ?? indexed?.associatedCreator ?? row.associatedCreator ?? null,
    marketTitle: market?.title ?? indexed?.marketTitle ?? row.marketTitle,
    outcomeLabel: outcome?.label ?? indexed?.outcomeLabel ?? row.outcomeLabel,
    outcomeImageUrl: outcome?.imageUrl ?? indexed?.outcomeImageUrl ?? row.outcomeImageUrl,
    outcomeImageNsfw: outcome?.imageNsfw ?? indexed?.outcomeImageNsfw ?? row.outcomeImageNsfw,
  };
}
